<template>
  <editor ref="editor" :class="editing" :config="getConfig()" :initialized="ready" />
</template>

<script>
import Header from '@editorjs/header';
import List from '@editorjs/list';
import CodeTool from '@editorjs/code';
import Paragraph from '@editorjs/paragraph';
import Embed from '@editorjs/embed';
import ImageTool from '@editorjs/image';
import Table from '@editorjs/table';
import Delimiter from '@editorjs/delimiter';
import InlineCode from '@editorjs/inline-code';

import { translate } from '@/services/translate.service';
import { uploadArticleImage } from '@/modules/article/article.service';

const SHORTCUTS = {
  HEADER: 'CMD+SHIFT+H',
  INLINE_CODE: 'CMD+SHIFT+M',
};

const EDITOR_MODES = {
  VIEW: 'view',
  EDIT: 'edit',
};

export const DEFAULT_CONFIG = (() => ({
  autofocus: true,
  i18n: {
    messages: {
      ui: {
        blockTunes: {
          toggler: {
            'Click to tune': `${translate('click-to-tune')}`,
            'or drag to move': `${translate('or-drag-to-move')}`,
          },
        },
        inlineToolbar: {
          converter: {
            'Convert to': `${translate('convert-to')}`,
          },
        },
        toolbar: {
          toolbox: {
            Add: `${translate('options')}`,
          },
        },
      },
      toolNames: {
        Text: `${translate('text')}`,
        Heading: `${translate('heading')}`,
        List: `${translate('list')}`,
        Image: `${translate('image')}`,
        Warning: `${translate('editor-warning')}`,
        Checklist: `${translate('checklist')}`,
        Quote: `${translate('quote')}`,
        Code: `${translate('code')}`,
        Delimiter: `${translate('delimiter')}`,
        'Raw HTML': `${translate('raw-html')}`,
        Table: `${translate('table')}`,
        Marker: `${translate('marker')}`,
        Bold: `${translate('bold')}`,
        Italic: `${translate('italic')}`,
        InlineCode: `${translate('inline-code')}`,
      },
      blockTunes: {
        delete: {
          Delete: `${translate('delete')}`,
        },
        moveUp: {
          'Move up': `${translate('move-up')}`,
        },
        moveDown: {
          'Move down': `${translate('move-down')}`,
        },
      },
      tools: {
        image: {
          'Select an Image': `${translate('select-an-image')}`,
        },
        list: {
          Unordered: `${translate('unordered')}`,
          Ordered: `${translate('ordered')}`,
        },
        link: {
          'Add a link': `${translate('add-a-link')}`,
        },
        table: {
          Heading: `${translate('heading')}`,
          'With headings': `${translate('with-headings')}`,
          'Without headings': `${translate('without-headings')}`,
          'Add column to left': `${translate('add-column-to-left')}`,
          'Add column to right': `${translate('add-column-to-right')}`,
          'Delete column': `${translate('delete-column')}`,
          'Add row above': `${translate('add-row-above')}`,
          'Add row below': `${translate('add-row-below')}`,
          'Delete row': `${translate('delete-row')}`,
        },
        code: {
          'Enter a code': `${translate('enter-a-code')}`,
        },
      },
    },
  },
  tools: {
    header: {
      class: Header,
      inlineToolbar: true,
      shortcut: SHORTCUTS.HEADER,
      config: {
        placeholder: `${translate('enter-title-here')}`,
        levels: [1, 2, 3, 4, 5, 6],
        defaultLevel: 1,
      },
    },
    paragraph: {
      class: Paragraph,
      inlineToolbar: true,
    },
    image: {
      class: ImageTool,
      config: {
        uploader: {
          uploadByFile(image) {
            return uploadArticleImage(image);
          },
        },
      },
    },
    list: {
      class: List,
      inlineToolbar: true,
    },
    table: {
      class: Table,
      inlineToolbar: true,
      config: {
        rows: 2,
        cols: 3,
      },
    },
    embed: {
      class: Embed,
      inlineToolbar: false,
    },
    code: CodeTool,
    inlineCode: {
      class: InlineCode,
      shortcut: SHORTCUTS.INLINE_CODE,
    },
    delimiter: Delimiter,
  },
}))();

export default {
  name: 'DaraEditor',
  props: {
    article: {
      type: Object,
    },
    mode: {
      type: String,
      default: 'view',
      validator(mode) {
        return mode && Object.values(EDITOR_MODES).includes(mode);
      },
    },
    change: {
      type: Function,
      default: () => {},
    },
    ready: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    editing() {
      return this.mode === 'edit' ? 'editor' : '';
    },
  },
  methods: {
    getConfig() {
      return {
        ...DEFAULT_CONFIG,
        ...this.getBlankConfig(),
        onChange: this.onChange,
      };
    },
    getBlankConfig() {
      return {
        readOnly: this.mode === EDITOR_MODES.VIEW,
        data: {
          time: new Date().getTime(),
          blocks: this.article?.content || [
            {
              type: 'header',
              data: { level: 1 },
            },
            {
              type: 'paragraph',
            },
          ],
        },
      };
    },
    onChange() {
      if (this.change) {
        this.$refs.editor._data.state.editor.save().then(this.change);
      }
    },
  },
};
</script>
<style lang="scss">
@import '@/components/editor/Editor';

  .editor {
    padding-bottom: 100px !important;
    padding-left: 30px !important;
    padding-right: 30px !important;
    border-radius: 5px;
    box-shadow: 1px 1px 5px 1px rgb(230, 230, 230);
  }
</style>
